import { REPORT_CATEGORY_OPTIONS, REPORTS } from '@/app/dashboards/utils/reports.enum';
import {
  GRAPH_DISPLAY_OPTIONS,
  GRAPH_DISPLAY_OPTIONS_FOR_MENU,
  METRIC_DISPLAY_OPTIONS,
} from '@/models/dashboards/aggregate-type.enum';

export const BAR_VISUALIZATIONS = Object.freeze([
  GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value,
  GRAPH_DISPLAY_OPTIONS.STACKED_BAR_GRAPH.value,
  GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
  GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value,
]);

export const STACKED_BAR_REPORT_TYPES = Object.freeze([
  REPORTS.STACKED_BAR_METRIC.value,
  REPORTS.ADS_STACKED_BAR_METRIC.value,
  REPORTS.STACKED_BAR_METRIC_TIME_SERIES.value,
]);

// Remove canAccessStatsEngineV2 parameter when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
export function getReportVisualizationBasedOnReportType(
  reportType,
  reportCategory,
  canAccessStatsEngineV2 = false,
) {
  const defaultResult = {
    reportType,
    reportVisualization: null,
  };

  // Remove when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag — collapse to VERTICAL_BAR_CHART
  const barChartVisualization = canAccessStatsEngineV2
    ? GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value
    : GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value;
  const stackedBarVisualization = canAccessStatsEngineV2
    ? GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value
    : GRAPH_DISPLAY_OPTIONS.STACKED_BAR_GRAPH.value;

  if (reportCategory === REPORT_CATEGORY_OPTIONS.OWNED.id) {
    switch (reportType) {
      case REPORTS.TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };

      case REPORTS.MULTI_METRIC_MEDIA_TYPE.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };

      case REPORTS.TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };

      case REPORTS.GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };

      case REPORTS.BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: barChartVisualization,
        };

      case REPORTS.STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: stackedBarVisualization,
        };

      case REPORTS.CONTENT_OWNED.value:
      case REPORTS.CONTENT.value:
        return {
          reportType: REPORTS.CONTENT_OWNED.value,
          reportVisualization: null,
        };
      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.EARNED.id) {
    switch (reportType) {
      case REPORTS.TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };
      case REPORTS.CREATOR_TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };
      case REPORTS.MULTI_METRIC_MEDIA_TYPE.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };
      case REPORTS.TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };
      case REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };
      case REPORTS.GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };
      case REPORTS.CREATOR_LINE_GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };
      case REPORTS.BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: barChartVisualization,
        };
      case REPORTS.CREATOR_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: barChartVisualization,
        };
      case REPORTS.STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: stackedBarVisualization,
        };
      case REPORTS.CREATOR_CONTENT.value:
        return {
          reportType: REPORTS.CONTENT_UGC.value,
          reportVisualization: null,
        };
      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.COMPETITIVE.id) {
    switch (reportType) {
      case REPORTS.COMPETITIVE_GRAPH.value:
        return {
          reportType: REPORTS.COMPETITIVE_GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };
      case REPORTS.COMPETITIVE_BAR_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: barChartVisualization,
        };
      case REPORTS.COMPETITIVE_CONTENT.value:
        return {
          reportType: REPORTS.COMPETITIVE_CONTENT.value,
          reportVisualization: null,
        };
      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id) {
    switch (reportType) {
      case REPORTS.ADS_TOTAL_METRIC.value:
        return {
          reportType: REPORTS.ADS_TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };
      case REPORTS.ADS_TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.ADS_TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };
      case REPORTS.ADS_GRAPH.value:
        return {
          reportType: REPORTS.ADS_GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };
      case REPORTS.ADS_BAR_METRIC.value:
        return {
          reportType: REPORTS.ADS_GRAPH.value,
          reportVisualization: barChartVisualization,
        };
      case REPORTS.ADS_STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.ADS_GRAPH.value,
          reportVisualization: stackedBarVisualization,
        };
      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.COMMUNITY.id) {
    switch (reportType) {
      case REPORTS.TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };

      case REPORTS.TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };

      case REPORTS.GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };

      case REPORTS.BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      case REPORTS.STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.TSI.id) {
    switch (reportType) {
      case REPORTS.TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };

      case REPORTS.TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };

      case REPORTS.GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };

      case REPORTS.BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      case REPORTS.STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      case REPORTS.DOUGHNUT_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.DOUGHNUT_CHART.value,
        };
      default:
        return defaultResult;
    }
  }

  if (reportCategory === REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id) {
    switch (reportType) {
      case REPORTS.TOTAL_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TOTAL.value,
        };

      case REPORTS.TOTAL_GROUPED_METRIC.value:
        return {
          reportType: REPORTS.TOTAL_METRIC.value,
          reportVisualization: METRIC_DISPLAY_OPTIONS.TABLE.value,
        };

      case REPORTS.GRAPH.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS.LINE_CHART.value,
        };

      case REPORTS.BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      case REPORTS.STACKED_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value,
        };

      case REPORTS.HORIZONTAL_BAR_METRIC.value:
        return {
          reportType: REPORTS.GRAPH.value,
          reportVisualization: GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value,
        };

      case REPORTS.CONTENT.value:
        return {
          reportType: REPORTS.CONTENT.value,
          reportVisualization: null,
        };
      default:
        return defaultResult;
    }
  }
  return defaultResult;
}
